/**
 * DeleteImpactModal — Modal de confirmación de eliminación con advertencia de impacto.
 *
 * Si el elemento tiene planes de ejecución asociados, muestra un resumen
 * de los planes afectados antes de confirmar el borrado.
 */
import React from 'react';

interface ImpactPlan {
  id: string;
  name: string;
  runs_count: number;
}

export interface ImpactData {
  plans: ImpactPlan[];
  total_runs: number;
  total_cases?: number;
  total_scenarios?: number;
  total_requirements?: number;
}

interface DeleteImpactModalProps {
  entityType: 'caso' | 'escenario' | 'requerimiento' | 'version' | 'sistema';
  entityName: string;
  impact: ImpactData;
  onConfirm: () => void;
  onCancel: () => void;
}

const TYPE_ICON: Record<string, string> = {
  caso: '◻',
  escenario: '◈',
  requerimiento: '▣',
  version: '⧉',
  sistema: '⬡',
};

export function DeleteImpactModal({
  entityType, entityName, impact, onConfirm, onCancel,
}: DeleteImpactModalProps) {
  const hasImpact = impact.total_runs > 0;
  const icon = TYPE_ICON[entityType] ?? '◻';

  // Build impact summary lines
  const summaryLines: string[] = [];
  if (impact.total_requirements) summaryLines.push(`${impact.total_requirements} requerimiento${impact.total_requirements !== 1 ? 's' : ''}`);
  if (impact.total_scenarios) summaryLines.push(`${impact.total_scenarios} escenario${impact.total_scenarios !== 1 ? 's' : ''}`);
  if (impact.total_cases) summaryLines.push(`${impact.total_cases} caso${impact.total_cases !== 1 ? 's' : ''} de prueba`);

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => e.target === e.currentTarget && onCancel()}
    >
      <div style={{ width: 480, borderRadius: 16, background: 'var(--color-background-primary)', border: '1px solid var(--color-border-secondary)', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--color-border-tertiary)', display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: hasImpact ? '#FAEEDA' : '#FCEBEB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
            {hasImpact ? '⚠' : '🗑'}
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--color-text-primary)' }}>
              Eliminar {entityType}
            </div>
            <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 3 }}>
              <span style={{ fontSize: 13 }}>{icon}</span>{' '}
              <span style={{ fontWeight: 600 }}>{entityName}</span>
            </div>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '18px 22px' }}>
          {hasImpact ? (
            <>
              {/* Impact warning */}
              <div style={{ padding: '12px 14px', borderRadius: 10, background: '#FAEEDA', border: '1px solid #F5C97E', marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#5F3B00', marginBottom: 6 }}>
                  Este {entityType} tiene historial de ejecuciones
                </div>
                <div style={{ fontSize: 12, color: '#6B4400', lineHeight: 1.5 }}>
                  Al eliminar se perderán <strong>{impact.total_runs} ejecución{impact.total_runs !== 1 ? 'es' : ''}</strong> y todas las evidencias asociadas.
                  {summaryLines.length > 0 && (
                    <span> También se eliminarán {summaryLines.join(', ')}.</span>
                  )}
                </div>
              </div>

              {/* Affected plans */}
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                Planes de ejecución afectados
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16, maxHeight: 180, overflowY: 'auto' }}>
                {impact.plans.map(plan => (
                  <div key={plan.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderRadius: 8, background: 'var(--color-background-secondary)', border: '1px solid var(--color-border-tertiary)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 14 }}>▷</span>
                      <span style={{ fontSize: 13, color: 'var(--color-text-primary)', fontWeight: 500 }}>{plan.name}</span>
                    </div>
                    <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)', padding: '2px 8px', borderRadius: 4, background: 'var(--color-background-primary)' }}>
                      {plan.runs_count} ejecución{plan.runs_count !== 1 ? 'es' : ''}
                    </span>
                  </div>
                ))}
              </div>

              <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 4 }}>
                Esta acción <strong>no se puede deshacer</strong>.
              </div>
            </>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 16 }}>
              ¿Estás seguro que deseas eliminar este {entityType}?
              {summaryLines.length > 0 && (
                <span> Se eliminarán también {summaryLines.join(', ')}.</span>
              )}
              {' '}Esta acción <strong>no se puede deshacer</strong>.
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 22px', borderTop: '1px solid var(--color-border-tertiary)', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid var(--color-border-secondary)', background: 'transparent', cursor: 'pointer', fontSize: 13, color: 'var(--color-text-secondary)' }}
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: '#E24B4A', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
          >
            {hasImpact ? '⚠ Eliminar de todas formas' : 'Eliminar'}
          </button>
        </div>
      </div>
    </div>
  );
}